/* eslint-disable tailwindcss/no-contradicting-classname */

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useUserProfile } from '@/context/UserProfileContext';
import { db } from '@/firebase';
import type { ProfileSummary } from '@/types/profile';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonRouterLink, IonToolbar } from '@ionic/react';
import { getCountryData, type TCountryCode } from 'countries-list';
import { get, push, ref, set } from 'firebase/database';
import { gridOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import AvatarUpload from './AvatarUpload';
import FriendshipButton from './FriendshipButton';
import GolfIcon from './GolfIcon';
import LinksList from './LinksList';
import NeoMap from './NeoMap';
import SectionTitle from './SectionTitle';
import ShareDialog from './ShareDialog';
import { SocialLinks } from './SocialLinks';
import { Button } from './ui/button';
import { GolfAvatar } from './ui/GolfAvatar';
import UserBag from './UserBag';
import { UserProfileMedia } from './UserProfileMedia';
type ProfilePageProps = {
  edit?: boolean;
  data: ProfileSummary;
  profileDetailed?: ProfileSummary;
};

export default function UserProfile({ edit, data, profileDetailed }: ProfilePageProps) {
  document.title = data.profile.username;
  const { profile: currentUserProfile } = useUserProfile();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const profileHeaderRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  // Set up intersection observer for sticky header with animation
  useEffect(() => {
    if (!profileHeaderRef.current || !headerRef.current) return;

    // Calculate the rootMargin based on the avatar size
    // We use a larger margin to avoid the "dead zone" that causes flickering
    const rootMargin = '-160px 0px 0px 0px';

    // Debounce timer and state tracking
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastKnownState = isHeaderSticky;
    let consecutiveCount = 0;

    const updateStickyHeader = (isSticky: boolean) => {
      // If state is already what we want, do nothing
      if ((isHeaderSticky && isSticky) || (!isHeaderSticky && !isSticky)) {
        return;
      }

      // Check if this is a rapid toggle (potential flicker)
      if (isSticky !== lastKnownState) {
        consecutiveCount = 0; // Reset counter on state change
      } else {
        consecutiveCount++;
      }

      lastKnownState = isSticky;

      // Clear any existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce the state change to prevent flickering
      // Use a shorter delay if we've seen multiple consecutive same states
      const delay = consecutiveCount > 2 ? 50 : 150;

      debounceTimer = setTimeout(() => {
        // Only update if we're still in the same desired state
        if (lastKnownState === isSticky) {
          // Start animation
          setIsAnimating(true);

          // Update state which will change the layout
          setIsHeaderSticky(isSticky);

          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            // Allow animation to complete
            setTimeout(() => {
              setIsAnimating(false);
            }, 300); // Match duration in CSS
          });
        }
      }, delay);
    };

    // Create the observer with a threshold array for smoother transitions
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // The ratio tells us how much of the element is visible
          // Adding hysteresis: require more visibility to switch back to non-sticky
          if (isHeaderSticky && entry.intersectionRatio > 0.15) {
            updateStickyHeader(false); // Switch to non-sticky only when clearly visible
          } else if (!isHeaderSticky && !entry.isIntersecting) {
            updateStickyHeader(true); // Switch to sticky when not visible
          }
        });
      },
      { threshold: [0, 0.1, 0.15, 0.2], rootMargin }
    );

    observer.observe(profileHeaderRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isHeaderSticky]);

  // Check if user is authenticated
  useEffect(() => {
    // If currentUserProfile exists, the user is authenticated
    setIsSignedIn(!!currentUserProfile);
  }, [currentUserProfile]);

  const { t } = useTranslation();
  const [userId, setUserId] = useState<string | null>(null);
  const { profile, membershipCourse, trips, posts } = data;

  useEffect(() => {
    console.log(posts)
    const getCurrentUser = async () => {
      const result = await FirebaseAuthentication.getCurrentUser();
      setUserId(result.user?.uid || null);
    };
    getCurrentUser();
  }, []);

  // Fonction pour gérer le clic sur le bouton de message
  const handleMessageClick = async (targetUserId: string) => {
    if (!userId) return;

    try {
      // Vérifier si une conversation existe déjà entre les deux utilisateurs
      const chatsRef = ref(db, 'chats');
      const snapshot = await get(chatsRef);

      let conversationId = null;

      if (snapshot.exists()) {
        // Parcourir toutes les conversations pour trouver une conversation directe entre les deux utilisateurs
        const chats = snapshot.val();
        for (const chatId in chats) {
          const chat = chats[chatId];
          if (chat.type === 'direct' &&
            chat.participants.length === 2 &&
            chat.participants.includes(userId) &&
            chat.participants.includes(targetUserId)) {
            conversationId = chatId;
            break;
          }
        }
      }

      // Si aucune conversation n'existe, en créer une nouvelle
      if (!conversationId) {
        const newChatRef = push(chatsRef);
        conversationId = newChatRef.key;

        // Créer la nouvelle conversation
        await set(newChatRef, {
          type: 'direct',
          participants: [userId, targetUserId],
          lastMessageTime: new Date().toISOString(),
          createdAt: new Date().toISOString()
        });
      }

      // Rediriger vers la page de messages avec l'ID de conversation
      history.push(`/messages/c/${conversationId}`);
    } catch (error) {
      console.error('Erreur lors de la création/récupération de la conversation:', error);
    }
  };

  // Avatar image parameters
  const params = new URLSearchParams();
  params.set('height', '256');
  params.set('width', '256');
  params.set('quality', '90');
  const avatarSrc = `${profile.imageUrl}?${params.toString()}`;

  // Get visited countries
  const uniqueCountries = new Set<string>();
  Object.values(trips).forEach((year: any) => {
    if (Array.isArray(year)) {
      year.forEach((trip: any) => {
        if (trip.course?.country) {
          uniqueCountries.add(trip.course.country);
        }
      });
    } else if (year.course?.country) {
      uniqueCountries.add(year.course.country);
    }
  });
  const visitedCountries = Array.from(uniqueCountries);

  // Total trips calculation
  const totalTrips = Object.values(trips).reduce((total, year) => {
    if (Array.isArray(year)) {
      return total + year.length;
    }
    return total + 1;
  }, 0);

  return (
    <>
      <IonHeader>
        <div
          ref={headerRef}
          className={`fixed top-0 left-0 right-0 z-50 bg-background md:hidden transition-all duration-300 pt-safe ease-out ${isHeaderSticky ? 'opacity-100 h-auto sticky' : 'opacity-0 pointer-events-none'}`}
        >
          <div className="flex items-center h-full ml-4 px-4 relative">
            {/* Back button - always visible */}
            <IonButtons slot="start" className="absolute left-2 z-10">
              <IonBackButton color="" text="" className='text-foreground' defaultHref="/dashboard" />
            </IonButtons>

            {/* Avatar with transition class */}
            <div
              className={`relative overflow-hidden rounded-full transition-all duration-300 ease-out ${isAnimating ? 'animate-avatar' : ''} w-8 h-8 ml-8`}
            >
              <img
                src={avatarSrc}
                alt={profile.username}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `/assets/images/avatar-placeholder.png`;
                }}
              />
            </div>

            {/* Username with transition class */}
            <div
              className={`ml-3 font-medium transition-all duration-300 ease-out ${isHeaderSticky ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              {`${profile?.firstName} ${profile.lastName}`}
            </div>

            {/* Right side buttons */}
            <div className="ml-auto">
              {isSignedIn && profileDetailed && (
                <IonButton fill="clear" routerLink='/menu'>
                  <IonIcon className='size-6 text-foreground' icon={gridOutline} />
                </IonButton>
              )}
            </div>
          </div>
        </div>
      </IonHeader>
      <IonContent fullscreen={true}>
        <div className="flex w-full flex-col md:rounded-lg mx-auto max-w-4xl">
          <div ref={profileHeaderRef} id="profile-header" className="relative md:to-background bg-gradient-to-t to-primary/20 dark:to-foreground/20 from-transparent pt-safe md:mt-0">

            {/* Origin Toolbar */}
            <IonToolbar className='px-3 md:hidden'>
              <IonButtons slot="start">
                <IonBackButton color="" text="" className='text-white bg-black/10 rounded-full p-1 px-2' defaultHref="/dashboard" />
              </IonButtons>
              <IonButtons slot="end">
                {isSignedIn && profileDetailed && (
                  <IonButton fill="clear" routerLink='/menu'>
                    <IonIcon className='size-6 text-foreground' icon={gridOutline} />
                  </IonButton>
                )}
              </IonButtons>
            </IonToolbar>

            {/* Avatar and Profile Info */}
            <div className="block px-4 pb-6 md:relative md:px-6">
              <div className="flex justify-center md:justify-start">
                {(edit || !userId || userId !== profile.userId) ? (
                  <GolfAvatar
                    canOpen={!edit}
                    size={128}
                    imageUrl={avatarSrc}
                    name={profile.firstName || 'UK'}
                    className="border-2 border-card md:size-[128px]"
                  />
                ) : (
                  <AvatarUpload
                    initialImage={avatarSrc}
                    className="size-32 border-2 border-card md:size-[128px]"
                  />
                )}
              </div>
              <div className="flex flex-col justify-between md:flex-row">
                <div className="p-3 md:p-0">
                  <div className="flex items-end justify-center text-center md:justify-start">
                    <h1 ref={nameRef} className="text-3xl font-bold">
                      {/* <Badge className="pointer-events-none mt-1 bg-[#003C80] text-white">Pro</Badge> */}
                      {`${profile?.firstName} ${profile.lastName}`}
                    </h1>
                    {profile.verified && <img src="/assets/images/icons/verified.svg" className='size-8 ml-1 rounded-full' />}
                  </div>

                  <div className="flex flex-row justify-center md:justify-start">
                    {profile.countryCode && (
                      <div className="flex items-center text-lg text-muted-foreground gap-2">
                        <img
                          src={`/assets/images/flags/${profile.countryCode}.svg`}
                          alt={profile.countryCode}
                          className="w-8 h-6 object-cover rounded"
                        />
                        <span>
                          {getCountryData(profile.countryCode as TCountryCode).name}
                        </span>

                      </div>
                    )}
                  </div>
                  <p className="text-center text-muted-foreground md:text-left">
                    {profile?.description}
                    {membershipCourse?.name}
                  </p>
                </div>
                <div className="flex flex-row items-center justify-center space-x-1 text-center md:justify-end">
                  {userId === profile.userId ? (
                    <>
                      <IonButton shape='round' fill='solid' routerLink='/settings/edit-profile' type="button" color='transparent' className="text-secondary bg-foreground rounded-full">
                        {t('edit_profile')}
                      </IonButton>
                      <ShareDialog username={profile.username} />
                    </>
                  ) : (
                    <>
                      {userId && (
                        <>
                          <FriendshipButton userId={profile.userId} />
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleMessageClick(profile.userId)}
                          >
                            <GolfIcon name="message" className="mr-2" />
                            {t('message')}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                          >
                            <GolfIcon name="more" />
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <SocialLinks profile={profile} />
          </div>

          {/* Basic Summary - Apple Health Style Cards */}
          <div className="p-4">
            <div className="hidden grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Average Score Card */}
              <div className="bg-card rounded-xl p-4 shadow">
                <h3 className="text-sm text-muted-foreground mb-1">Average Score</h3>
                <div className="flex flex-col">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-semibold">{profile.handicap || 68}</span>
                    <span className="text-muted-foreground text-lg ml-1">.531</span>
                  </div>
                  <div className="mt-2 h-20">
                    <div className="flex h-full items-end space-x-1">
                      {[0.4, 0.8, 0.5, 0.7, 0.4, 0.3, 0.6].map((height, i) => (
                        <div
                          key={i}
                          className="bg-primary/60 w-full rounded-t-sm"
                          style={{ height: `${height * 100}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rounds Played Card */}
              <div className="bg-card rounded-xl p-4 shadow">
                <h3 className="text-sm mb-1">Rounds Played</h3>
                <div className="flex flex-col">
                  <span className="text-5xl font-semibold">{totalTrips || 1830}</span>
                  <div className="mt-2 h-20">
                    <div className="relative h-full">
                      <svg viewBox="0 0 100 30" className="h-full w-full">
                        <path
                          d="M0,15 L14,5 L28,10 L42,8 L56,12 L70,7 L84,11 L100,15"
                          fill="none"
                          stroke="#000000"
                          strokeWidth="1"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driving Distance Card */}
              <div className="bg-card rounded-xl p-4 shadow">
                <h3 className="text-sm text-muted-foreground mb-1">Driving Distance</h3>
                <div className="flex flex-col">
                  <span className="text-5xl font-semibold">325<span className="text-3xl">.8</span></span>
                  <div className="mt-2 h-20">
                    <div className="relative h-full bg-gradient-to-t from-lime-200/50 to-transparent rounded-md">
                      <svg viewBox="0 0 100 30" className="h-full w-full">
                        <path
                          d="M0,20 L20,15 L40,22 L60,18 L80,20 L100,5"
                          fill="none"
                          stroke="#888"
                          strokeWidth="1"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Started Year Card - Only show if available */}
              {profile.golfStartYear && (
                <div className="bg-card rounded-xl p-4 shadow">
                  <h3 className="text-sm text-muted-foreground mb-1">Started Year</h3>
                  <div className="flex flex-col">
                    <span className="text-5xl font-semibold">{profile.golfStartYear}</span>
                    <div className="mt-2 h-20 flex items-center justify-center">
                      <span className="text-xl text-muted-foreground">{new Date().getFullYear() - profile.golfStartYear} years</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <Tabs defaultValue="infos" className="w-full">
            <TabsList color="primary" className="grid w-full grid-cols-2 sticky -top-0.5 z-20 dark:bg-black border-y border-t-0">
              <TabsTrigger value="infos" className="flex items-center gap-2">
                <GolfIcon name="user-square" className="size-6" />
                <span className="hidden md:inline">{t('profile')}</span>
              </TabsTrigger>
              <TabsTrigger value="medias">
                <GolfIcon name="album" className="size-6" />
                <span className="hidden md:inline">{t('medias')}</span>
              </TabsTrigger>
            </TabsList>

            {/* Infos Tab */}
            <TabsContent value="infos">
              <div className="space-y-6 p-4">
                <LinksList profileId={profile.id} />

                <SectionTitle>{t('played_courses')}</SectionTitle>
                <NeoMap visitedCountries={visitedCountries} />

                <UserBag userId={profile.id.toString()} limit={4} showEditButton={profile.id === currentUserProfile?.id} />

                <SectionTitle>{t('recent_rounds')}</SectionTitle>
                {/* <RecentScorecards /> */}
              </div>
            </TabsContent>

            {/* Medias Tab */}
            <TabsContent value="medias">
              <div className="text-center text-xl text-muted-foreground">
                {posts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-4 md:hidden">
                    <div className="flex h-full gap-2 flex-col items-center justify-center">
                      <img src="/assets/images/NoImages.svg" alt="No messages" />
                      <p className="text-lg font-medium text-foreground">{t('no_images_yet')}</p>
                    </div>
                  </div>
                ) : (
                  <UserProfileMedia posts={posts} />
                )}
              </div>
            </TabsContent>
          </Tabs>
          {edit || userId
            ? (<div className="py-8"></div>)
            : (
              <div className="m-auto p-8 align-middle">
                <IonRouterLink href="/sign-up">
                  <Button type="button" className="relative inline-flex justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-slate-800 to-slate-700 px-3.5 py-2.5 text-sm font-medium text-slate-200 shadow before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,theme(colors.white/.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:[transition:background-position_0s_ease] hover:before:bg-[position:-100%_0,0_0] focus:outline-none focus:ring focus:ring-slate-500/50 focus-visible:outline-none focus-visible:ring focus-visible:ring-slate-500/50 dark:from-slate-200 dark:to-slate-100 dark:text-slate-800 dark:before:bg-[linear-gradient(45deg,transparent_25%,theme(colors.white)_50%,transparent_75%,transparent_100%)] dark:hover:bg-slate-100">
                    {t('create_your_profile')}
                  </Button>
                </IonRouterLink>
              </div>
            )}
        </div>
      </IonContent>
    </>
  );
}